import { useEffect } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';
import { useMainMenuContext } from 'state/menu/main-menu';
import { isValidDayPart } from 'state/menu/main-menu/hooks/use-active-day-parts';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const parseSanityIds = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return;
  }

  const sanityIds = value.filter(isNonEmptyString);
  return sanityIds.length > 0 ? sanityIds : undefined;
};

/**
 * Parses Amplitude payload for Upsell Personalization.
 *
 * The supported shape is `{ sanityIds: string[] }`. Invalid, empty, and
 * non-string entries are ignored; no usable IDs returns `undefined`.
 */
export const parseUpsellPersonalizationSanityIds = (payload: unknown): string[] | undefined => {
  if (!payload || typeof payload !== 'object') {
    return;
  }

  const payloadRecord = payload as Record<string, unknown>;
  return parseSanityIds(payloadRecord.sanityIds);
};

/**
 * Hook for the Upsell Personalization A/B experiment.
 *
 * Assigned payloads can provide `sanityIds` for later FeatureMenu replacement.
 * Invalid or missing payloads return `undefined`; callers use `variant` to distinguish arms.
 *
 * Exposure uses the ExperimentsProvider built-in dedupe (same as other experiments).
 * Exposure logs by default when an assignment is available during Main Menu.
 * Outside Main Menu, assignment data remains available without exposure. Pass
 * `skipExposureLogging: true` when a consumer only needs to read the assignment.
 */
export const useUpsellPersonalizationExperiment = (
  skipExposureLogging = false
): {
  sanityIds?: string[];
  isLoading: boolean;
  variant?: string;
} => {
  const { getVariantValue, isLoading, logExposure } = useExperiments();

  const { activeDayParts } = useMainMenuContext();

  const isMainMenuActive = activeDayParts?.some(
    dayPart => isValidDayPart(dayPart) && dayPart.key.toLowerCase() === 'main menu'
  );

  const variantData = getVariantValue(RunningExperiments.UpsellPersonalization);
  const sanityIds = parseUpsellPersonalizationSanityIds(variantData?.payload);
  const variantValue = variantData?.value;

  useEffect(() => {
    if (skipExposureLogging || !variantValue || !isMainMenuActive) {
      return;
    }

    logExposure(RunningExperiments.UpsellPersonalization);
  }, [isMainMenuActive, logExposure, skipExposureLogging, variantValue]);

  return {
    sanityIds,
    isLoading,
    variant: variantValue,
  };
};
